import React, { useState } from "react";
import { Send, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GoldButton, Eyebrow, SectionTitle } from "@/components/shared";
import { Ornament } from "@/components/Ornament";
import client, { formatApiError } from "@/lib/api";

const EMPTY = { name: "", phone: "", email: "", address: "", zip: "", city: "Basel", service: "", property: "", date: "", size: "", details: "" };

// Full clearance request form (date, address, service type, property type …).
export const RaeumungForm = () => {
  const { t, lang } = useLang();
  const r = t.request;
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service || !form.property || !form.date) {
      toast.error(r.choose);
      return;
    }
    setBusy(true);
    try {
      await client.post("/clearance", { ...form, lang });
      toast.success(r.success);
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || (t.contact.form.error || "Fehler"));
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full bg-cream border border-border rounded-xl px-4 py-3 font-sans text-espresso placeholder:text-espresso/40 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all";
  const labelCls = "block text-xs tracking-widest uppercase text-espresso/50 mb-2 font-sans";

  return (
    <section id="anfrage" className="py-24 md:py-32 bg-sand grain-overlay scroll-mt-24" data-testid="raeumung-section">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <div className="text-center mb-10" data-reveal>
          <Eyebrow>{r.eyebrow}</Eyebrow>
          <SectionTitle>{r.title}</SectionTitle>
          <p className="mt-5 max-w-xl mx-auto text-espresso/65 font-sans leading-relaxed">{r.subtitle}</p>
          <Ornament className="mt-7" width={220} />
        </div>

        <form onSubmit={submit} data-testid="raeumung-form" className="bg-cream border border-border rounded-2xl p-7 md:p-10 shadow-[0_20px_60px_rgba(43,27,18,0.08)] space-y-6">
          <div className="grid sm:grid-cols-2 gap-5">
            <div><label className={labelCls}>{r.name} *</label><input required value={form.name} onChange={set("name")} className={field} data-testid="req-name" /></div>
            <div><label className={labelCls}>{r.phone} *</label><input required value={form.phone} onChange={set("phone")} className={field} data-testid="req-phone" /></div>
          </div>

          <div><label className={labelCls}>{r.email} *</label><input required type="email" value={form.email} onChange={set("email")} className={field} data-testid="req-email" /></div>

          <div className="grid sm:grid-cols-[1fr_140px_1fr] gap-5">
            <div><label className={labelCls}>{r.address} *</label><input required value={form.address} onChange={set("address")} className={field} data-testid="req-address" /></div>
            <div><label className={labelCls}>{r.zip}</label><input value={form.zip} onChange={set("zip")} className={field} data-testid="req-zip" /></div>
            <div><label className={labelCls}>{r.city} *</label><input required value={form.city} onChange={set("city")} className={field} data-testid="req-city" /></div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>{r.service} *</label>
              <Select value={form.service} onValueChange={set("service")} required>
                <SelectTrigger className={`${field} data-[placeholder]:text-espresso/40`} data-testid="req-service"><SelectValue placeholder={r.choose} /></SelectTrigger>
                <SelectContent>
                  {r.serviceOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelCls}>{r.property} *</label>
              <Select value={form.property} onValueChange={set("property")} required>
                <SelectTrigger className={`${field} data-[placeholder]:text-espresso/40`} data-testid="req-property"><SelectValue placeholder={r.choose} /></SelectTrigger>
                <SelectContent>
                  {r.propertyOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>{r.date} *</label>
              <div className="relative">
                <input required type="date" value={form.date} onChange={set("date")} className={`${field} pr-10`} data-testid="req-date" />
                <CalendarClock className="w-4 h-4 text-gold-deep absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
            <div><label className={labelCls}>{r.size}</label><input value={form.size} onChange={set("size")} className={field} data-testid="req-size" placeholder="z.B. 3.5 Zimmer" /></div>
          </div>

          <div><label className={labelCls}>{r.details}</label><textarea rows={4} value={form.details} onChange={set("details")} className={`${field} resize-none`} data-testid="req-details" /></div>

          <GoldButton as="button" type="submit" disabled={busy} data-testid="req-submit" className="w-full justify-center">{r.submit} <Send className="w-4 h-4" /></GoldButton>
        </form>
      </div>
    </section>
  );
};

export default RaeumungForm;
